import {
  Box,
  Container,
  Divider,
  Grid,
  Link as MuiLink,
  Stack,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { Logo } from "@/components/brand/Logo";
import { useAuth } from "@/auth/AuthContext";

type FooterLink = { label: string; to: string };
type FooterColumn = { title: string; items: FooterLink[] };

// Parallel role model: we pick *one* "audience column" at a time
// (Patients OR Doctors OR Admin) based on who is looking at the site,
// then always show the Company and Legal columns.
const patientColumn: FooterColumn = {
  title: "Patients",
  items: [
    { label: "Find a doctor", to: "/doctors" },
    { label: "Book appointment", to: "/doctors" },
    { label: "My appointments", to: "/patient/appointments" },
  ],
};

const doctorColumn: FooterColumn = {
  title: "Doctors",
  items: [
    { label: "Dashboard", to: "/doctor/dashboard" },
    { label: "Appointments", to: "/doctor/appointments" },
    { label: "Availability", to: "/doctor/schedule" },
    { label: "Onboarding", to: "/doctor/onboarding" },
  ],
};

const adminColumn: FooterColumn = {
  title: "Admin",
  items: [{ label: "Doctors", to: "/admin/doctors" }],
};

// Shown to guests because doctor signup is a key conversion funnel.
const forDoctorsColumn: FooterColumn = {
  title: "Doctors",
  items: [
    { label: "For doctors", to: "/for-doctors" },
    { label: "Doctor login", to: "/doctor/dashboard" },
    { label: "Verification", to: "/for-doctors" },
  ],
};

const companyColumn: FooterColumn = {
  title: "Company",
  items: [
    { label: "About", to: "/about" },
    { label: "How it works", to: "/how-it-works" },
    { label: "Contact", to: "/contact" },
  ],
};

const legalColumn: FooterColumn = {
  title: "Legal",
  items: [
    { label: "Privacy", to: "/privacy" },
    { label: "Delete account", to: "/delete-account" },
    { label: "Terms", to: "/terms" },
    { label: "Refund policy", to: "/refunds" },
  ],
};

export function Footer() {
  const { isAuthenticated, isDoctor, isAdmin, isPatient } = useAuth();
  const year = new Date().getFullYear();

  // Build the column list top-down so each role sees only what is
  // relevant to them; nothing "leaks" across roles.
  const columns: FooterColumn[] = [];
  if (!isAuthenticated) {
    columns.push(patientColumn, forDoctorsColumn);
  } else if (isDoctor) {
    columns.push(doctorColumn);
  } else if (isAdmin) {
    columns.push(adminColumn);
  } else if (isPatient) {
    columns.push(patientColumn);
  }
  columns.push(companyColumn, legalColumn);

  // Each column takes ~2/12 on md+, so the brand block adjusts to fill
  // the remaining space.
  const brandSpan = Math.max(12 - columns.length * 2, 4);

  return (
    <Box
      component="footer"
      sx={{
        mt: 10,
        pt: 6,
        pb: 4,
        bgcolor: "#0D2815",
        color: "rgba(255,255,255,0.82)",
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          <Grid item xs={12} md={brandSpan}>
            <Box sx={{ mb: 2 }}>
              <Logo size={38} />
            </Box>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
              Online doctor consultation across India — book verified doctors,
              consult over video, pay securely. Your care, one link away.
            </Typography>
          </Grid>

          {columns.map((c) => (
            <Grid key={c.title} item xs={6} md={2}>
              <FooterCol title={c.title} items={c.items} />
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 4, borderColor: "rgba(255,255,255,0.12)" }} />

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
        >
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>
            © {year} ChikitsaLink. Made with care in India.
          </Typography>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>
            Not for emergencies. In case of a medical emergency, dial 112.
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
}

function FooterCol({
  title,
  items,
}: {
  title: string;
  items: FooterLink[];
}) {
  return (
    <Stack spacing={1.25}>
      <Typography variant="subtitle2" sx={{ color: "#A5D6A7", mb: 0.5 }}>
        {title}
      </Typography>
      {items.map((i) => (
        <MuiLink
          key={i.to + i.label}
          component={RouterLink}
          to={i.to}
          sx={{
            color: "rgba(255,255,255,0.8)",
            fontSize: 14,
            "&:hover": { color: "#FFFFFF" },
          }}
        >
          {i.label}
        </MuiLink>
      ))}
    </Stack>
  );
}
